/**
 * Pure helpers of the conversation chat (`ConversationChat`): which stored rows the thread draws, the typing line and
 * the live-connection notice. Kept apart from the component so each rule is unit-tested.
 */
import type { ConversationSurface } from '@confluent/shared/lib/conversation-surface';
import type { ConversationEventsState } from '../../hooks/useConversationEvents.js';
import type { StreamMessage } from '../../hooks/useConversationStream.js';
import { parseUnifiedToolCallRow } from '@confluent/shared/lib/orchestrator/unified-tool-row';
import { isRenderableToolCall } from './ToolCallCard.js';

/**
 * The rows the thread draws. A tool row with `toolCalls`, or a turn's per-call row (`unified_tool_call`), is drawn by
 * the thread as tool-call parts; the intent drafter's JSON envelope is drawn by the chat itself as a `ToolCallCard`
 * (`renderMessage`). Any other envelope would print as raw JSON, so it is left out. The simplified surface never shows
 * plumbing at all: what the platform did is narrated by the assistant message's `behind_the_scenes` part.
 */
export function threadVisibleMessages(
  messages: readonly StreamMessage[],
  surface: ConversationSurface,
): readonly StreamMessage[] {
  return messages.filter((message) => {
    if (message.role !== 'tool') return true;
    if (surface === 'simple') return false;
    return (
      (message.toolCalls?.length ?? 0) > 0 ||
      parseUnifiedToolCallRow(message.content) !== null ||
      isRenderableToolCall(message.content)
    );
  });
}

type TypingUser = ConversationEventsState['typingUsers'][number];

/** Other people typing in this conversation (never the signed-in person), as one line; `null` when nobody is. */
export function typingLabel(users: readonly TypingUser[], currentUserId: string | null): string | null {
  const others = users.filter((user) => String(user.userId) !== currentUserId);
  if (others.length === 0) return null;
  if (others.length === 1) return `${others[0]?.displayName ?? 'Someone'} is typing…`;
  return `${others.length} people are typing…`;
}

/** What to tell the person about the live socket; `null` while it is fine (or not started). */
export function connectionNotice(status: ConversationEventsState['status']): string | null {
  if (status === 'reconnecting') return 'Live updates are reconnecting…';
  if (status === 'error') return 'Live updates are unavailable. Replies appear when the page refreshes.';
  return null;
}
